import { useEffect } from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { hashFn } from "@wagmi/core/query"
import { createConfig, createStorage, useAccount, useConfig, WagmiProvider } from "wagmi"
import { coinbaseWallet, injected, walletConnect } from "wagmi/connectors"

import type { IEmissionsConfig } from "@/domains/gauge"
import { ScaleAppContractAddresses } from "@/domains/application/scale"
import { useInitializeScaleApplication } from "@/domains/application/use-initialize-scale-application"
import { LcAddress } from "@/domains/core"
import { SystemTokenConfig } from "@/domains/tokens/token-repository"
import { useApiService } from "@/hooks/use-api-service"
import { useChainConfig } from "@/hooks/use-chain-config"
import { getChainFromConfig, getTransportFromConfig } from "@/lib/chain-config"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryKeyHashFn: hashFn,
    },
  },
})

const WALLET_CONNECT_PROJECT_ID =
  import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID ?? "37ff002895344fa2bf5c8f04e967ac8a"

const chain = getChainFromConfig()
const transport = getTransportFromConfig()
const storage = createStorage({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
})

const wagmiConfig = createConfig({
  chains: [chain],
  connectors: [
    injected(), // Auto-detects Rabby, Trust, MetaMask, and other injected wallets
    coinbaseWallet({
      appName: "Scale",
      preference: "all", // Supports both Smart Wallet and EOA
    }),
    walletConnect({
      projectId: WALLET_CONNECT_PROJECT_ID,
      showQrModal: true,
      metadata: {
        name: "Scale",
        description: "Scale Farm",
        url: "https://scale.farm",
        icons: ["https://scale.farm/favicon.png"],
      },
    }),
  ],
  transports: transport,
  ssr: true,
  storage,
  batch: {
    multicall: true,
  },
  syncConnectedChain: true,
})

const Providers = ({ children }: { children: React.ReactNode }) => {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <AppProvider>{children}</AppProvider>
      </QueryClientProvider>
    </WagmiProvider>
  )
}

export default Providers

const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const config = useConfig()

  const apiService = useApiService()
  const chainConfig = useChainConfig()
  const { address, isConnected, chainId: wagmiChainId, connector } = useAccount()

  const chainSlug = chainConfig.slug
  const chainId = chainConfig.chainId

  const systemTokenAddresses: SystemTokenConfig = {
    native: chainConfig.tokens.native,
    wrappedNative: chainConfig.tokens.wrappedNative.address,
    governance: chainConfig.tokens.governance.address,
  }

  const scaleAppContractAddresses: ScaleAppContractAddresses = {
    clFarmLensAddress: chainConfig.equalizer.lenses.clFarmLens,
    equalensMultiBalance: chainConfig.equalizer.lenses.multiBalance,
    equalens26: chainConfig.equalizer.lenses.equalens26,
    infoBribeByNft: chainConfig.equalizer.lenses.infoBribeByNft,
    scalensDefaultInfoPairs: chainConfig.equalizer.lenses.Frontend_DefaultPairInfo,
    scalensUserInfoPairs: chainConfig.equalizer.lenses.Frontend_UserPairInfo,
    scalensUserManageMinimalPoolInfo: chainConfig.equalizer.lenses.Frontend_UserManageMinimalPoolInfo,
    veInfo2: chainConfig.equalizer.contracts.veInfo2,
    veToken: chainConfig.tokens.vested.address,
    voter: chainConfig.equalizer.contracts.voter,
    superVoter: chainConfig.equalizer.contracts.superVoter,
    venAmm: chainConfig.equalizer.contracts.venAmm,
  }

  const emissionsConfig: IEmissionsConfig = {
    emitted: chainConfig.equalizer.emissions.lastSync.emitted,
    decayFactor: chainConfig.equalizer.emissions.lastSync.decayFactor,
    universalEpoch: chainConfig.equalizer.emissions.lastSync.universalEpoch,
  }

  const Scale = useInitializeScaleApplication({
    // @ts-ignore
    apiService,
    safeAddress: chainConfig.web3.safeAddress,
    chainSlug,
    scaleAppContractAddresses,
    // @ts-ignore
    config,
    chainId,
    systemTokenAddresses,
    routerAddress: chainConfig.equalizer.contracts.router,
    emissionsConfig,
  })

  if (typeof window !== "undefined") {
    // @ts-ignore
    window.Scale = Scale
    // @ts-ignore
    window.LcAddress = LcAddress
  }

  /**
   * Hook up the App and it's connection to the lock manager
   */
  useEffect(() => {
    if (!Scale) {
      return
    }
    if (address && isConnected && typeof address === "string") {
      void Scale.connectAccount(address, connector?.id ?? null, connector?.name ?? null, connector?.type ?? null)
    }
    return () => {
      Scale.disconnectAccount()
    }
  }, [address, isConnected, connector, Scale?.Wallet.Connection])

  useEffect(() => {
    if (Scale && wagmiChainId) {
      // Update chain ID when it changes
      Scale.Wallet.Connection.updateChainId(Number(wagmiChainId))
      console.log(`🔄 Wallet: Chain ID updated to ${wagmiChainId}`)
    }
  }, [wagmiChainId, Scale])

  return <>{children}</>
}
